import { FC, useEffect, useRef, useState } from 'react';
import { useOutsideClickDetection } from '../../../../hooks';
import { FilterRecallItemType } from '../../../../types/FilterTypes';
import DropdownSelect from '../../../shared/filters/dropdown-select/DropdownSelect';
import { DropdownWrapper, Icon } from '../../../style';
import RecallDropdown from './RecallDropdown';

type PortfolioRecallFilterProps = {
  options: readonly FilterRecallItemType[];
  values?: FilterRecallItemType[];
  onChange: (values: FilterRecallItemType[]) => void;
  onUpdate: (values: FilterRecallItemType[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
  isMobileDevice?: boolean;
};

const PortfolioRecallFilter: FC<PortfolioRecallFilterProps> = ({
  isMobileDevice,
  options,
  values,
  onChange,
  onUpdate,
  onOpen,
  onClose,
}) => {
  const wrapperRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [filteredItems, setFilteredItems] = useState<FilterRecallItemType[]>([...options]);
  const [filteredValues, setFilteredValues] = useState<FilterRecallItemType[]>([...values]);
  const [filterHeight, setFilterHeight] = useState<number>(0);

  useOutsideClickDetection(wrapperRef, setIsVisible);

  const label = (): string => {
    const selectedValuesLength = values?.length;
    let result: string;
    if (selectedValuesLength === options.length && options.length !== 0) {
      result = `All`;
    } else if (selectedValuesLength > 0) {
      result = `${values?.length} Sel.`;
    }

    return result;
  };

  const handleMouseEvent = (event: any): void => {
    if (event && event.target) {
      setFilterHeight(event.target.offsetParent?.offsetHeight || 0);
    }
  };

  const handleChange = (item: FilterRecallItemType): void => {
    if (values.find((value) => item.key === value.key)) {
      const newValues = values.filter((_item) => _item.key !== item.key);
      setFilteredValues(sortAndFilter(newValues, searchText));
      onChange(newValues);
    } else {
      const newValues = [...values, item];
      setFilteredValues(sortAndFilter(newValues, searchText));
      onChange(newValues);
    }
  };

  const isChecked = (item: FilterRecallItemType): boolean =>
    !!filteredValues.find((value) => item.key === value.key);

  const toggleSelection = (selectAll: boolean): void => {
    if (!selectAll) {
      const newValues = values.filter(
        (item) => !(filteredItems.findIndex((value) => item.key === value.key) > -1),
      );
      setFilteredValues(sortAndFilter(newValues, searchText));
      onChange(newValues);
    } else {
      const newValues = [...values, ...filteredItems];
      const updatedValues = newValues.filter(
        (item, pos) => newValues.findIndex((value) => item.key === value.key) === pos,
      );
      setFilteredValues(sortAndFilter(updatedValues, searchText));
      onChange(updatedValues);
    }
  };

  const sortAndFilter = (
    rawItems: FilterRecallItemType[],
    searchText: string,
  ): FilterRecallItemType[] => {
    const items = rawItems.filter((item) => {
      return Object.values(item).some((val) =>
        val.toString().toLowerCase().includes(searchText.toLowerCase()),
      );
    });

    // sort by the date in seconds in descending order
    items.sort(
      (a, b) =>
        new Date(b.date).getTime() - new Date(a.date).getTime() || a.key.localeCompare(b.key),
    );
    return items;
  };

  const handleDropdownWrapperClick = (): void => {
    setIsVisible(!isVisible);

    if (isVisible) {
      return;
    }

    if (options?.length === 0 && onUpdate) {
      onUpdate([]);
      return;
    }

    const filteredSelectedOptions = values.filter((x) => !!options.find((y) => y.key === x.key));
    if (filteredSelectedOptions?.length !== values?.length && onUpdate) {
      onUpdate(filteredSelectedOptions);
    }
  };

  useEffect(() => {
    const filteredOptions = sortAndFilter([...options], searchText);
    setFilteredItems(filteredOptions);
  }, [options, searchText]);

  useEffect(() => {
    const filteredOptions = sortAndFilter(values, searchText);
    setFilteredValues(filteredOptions);
  }, [values, searchText]);

  useEffect(() => {
    if (isVisible && onOpen) {
      onOpen();
    }
    if (!isVisible && onClose) {
      onClose();
    }
  }, [isVisible]);

  return (
    <>
      <DropdownWrapper data-testid='recall-filter' ref={wrapperRef} flexDirection='row' className={isMobileDevice ? 'responsive-adjust' : ''} isMobileDevice={isMobileDevice}>
        <div className='heap-filter-item w-88'>
          <span
            data-testid='multi-select-dropdown-header'
            onClick={handleDropdownWrapperClick}
            onMouseDown={handleMouseEvent}
          >
            <DropdownSelect name={'Recall'} inlineLabel={label()} />
          </span>
          {isVisible && (
            <RecallDropdown
              filteredItems={filteredItems}
              filterHeight={filterHeight}
              searchText={searchText}
              setSearchText={setSearchText}
              handleChange={handleChange}
              isChecked={isChecked}
              toggleSelection={toggleSelection}
              isMobileDevice={isMobileDevice}
            />
          )}
        </div>
        <div className='vertical-center'>
          <Icon type='dropdown' size={14} />
        </div>
      </DropdownWrapper>
    </>
  );
};

export default PortfolioRecallFilter;
